import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import * as Sentry from "@sentry/react";

import "./globals.css";
import { FirebaseAuthProvider } from "@/state/FirebaseAuthContext";
import { AnalyticsProvider } from "@/state/AnalyticsProvider";
import RootErrorBoundary from "./global-error";
import { lazy, Suspense, useMemo } from "react";
import { useVoiceInitialization } from "./hooks/useVoiceInitialization";
import { PeerbotsI18nProvider, PeerbotsLabels } from "@peerbots/core";
import { useLanguage } from "./hooks/useLanguage";

const DashLayout = lazy(() => import("./layouts/DashLayout"));
const HomePage = lazy(() => import("./pages/HomePage"));
const AuthorPage = lazy(() => import("./pages/AuthorPage"));
const ContentPage = lazy(() => import("./pages/ContentPage"));
const ControlPage = lazy(() => import("./pages/ControlPage"));
const ProfilePage = lazy(() => import("./pages/ProfilePage"));
const ReviewPage = lazy(() => import("./pages/ReviewPage"));
const ReviewSessionPage = lazy(() => import("./pages/ReviewSessionPage"));
const RobotPage = lazy(() => import("./pages/RobotPage"));
const FacePage = lazy(() => import("./pages/FacePage"));
const NotFound = lazy(() => import("./not-found"));

const SentryRoutes = Sentry.withSentryRouting(Routes);

function AppRoutes() {
  return (
    <Suspense
      fallback={
        <div className="flex h-screen w-screen items-center justify-center">
          Loading...
        </div>
      }
    >
      <SentryRoutes>
        <Route path="/face" element={<FacePage />} />
        <Route path="/dash" element={<DashLayout />}>
          <Route path="home" element={<HomePage />} />
          <Route path="author" element={<AuthorPage />} />
          <Route path="content" element={<ContentPage />} />
          <Route path="control" element={<ControlPage />} />
          <Route path="profile" element={<ProfilePage />} />
          <Route path="review" element={<ReviewPage />} />
          <Route path="review/session" element={<ReviewSessionPage />} />
          <Route path="robot" element={<RobotPage />} />
        </Route>
        <Route path="/" element={<Navigate to="/dash/home" replace />} />
        <Route path="*" element={<NotFound />} />
      </SentryRoutes>
    </Suspense>
  );
}

function PeerbotsI18nBridge({ children }: { children: React.ReactNode }) {
  const { currentLanguage, dir, t } = useLanguage();

  const labels: PeerbotsLabels = useMemo(
    () => ({
      auth: {
        signIn: t("auth.signIn"),
        signUp: t("auth.signUp"),
        resetPassword: t("auth.resetPassword"),
        email: t("auth.email"),
        emailLabel: t("auth.email"),
        password: t("auth.password"),
        passwordLabel: t("auth.password"),
        forgotPassword: t("auth.forgotPassword"),
        alreadyHaveAccount: t("auth.alreadyHaveAccount"),
        dontHaveAccount: t("auth.dontHaveAccount"),
        rememberedPassword: t("auth.rememberedPassword"),
        or: t("common.or"),
        signInWithGoogle: t("auth.signInWithGoogle"),
        signUpWithGoogle: t("auth.signUpWithGoogle"),
      },
      common: {
        select: t("common.select"),
        loading: t("common.loading"),
        close: t("common.close"),
        edit: t("common.edit"),
        delete: t("common.delete"),
        save: t("common.save"),
        cancel: t("common.cancel"),
      },
    }),
    [t],
  );

  return (
    <PeerbotsI18nProvider dir={dir} lang={currentLanguage} labels={labels}>
      {children}
    </PeerbotsI18nProvider>
  );
}

export default function App() {
  useVoiceInitialization();

  return (
    <RootErrorBoundary>
      <BrowserRouter>
        <AnalyticsProvider>
          <FirebaseAuthProvider>
            <PeerbotsI18nBridge>
              <AppRoutes />
            </PeerbotsI18nBridge>
          </FirebaseAuthProvider>
        </AnalyticsProvider>
      </BrowserRouter>
    </RootErrorBoundary>
  );
}
